import React, { useState, useEffect } from 'react';
import { useCompanyInfo } from '../../context/CompanyInfoContext';
import { useAuth } from '../../context/AuthContext';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SaveIcon } from 'lucide-react';
import Swal from 'sweetalert2';
import { useParams, useNavigate } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useLoading } from '../../context/LoadingContext';
import { useCountry } from '../../context/CountryContext';
import { useRoutePath } from '../../utils/getRoutePath';

const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const CompanyInfoForm = ({ initialData, isEditMode = false }) => {
  const getRoutePath = useRoutePath();  
  const { updateCompanyInfo, addCompanyInfo ,getCompanyInfoById} = useCompanyInfo();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [logoPreview, setLogoPreview] = React.useState('');
  const { countries } = useCountry();
  
  // Find the company info to edit if in edit mode
  const companyToEdit = React.useMemo(() => {
    
    if (id) {
      const found = getCompanyInfoById(id);
      
      return found;
    } 
    return null;
  }, [id,getCompanyInfoById]);

  // Initialize logo preview when company data is loaded
  React.useEffect(() => {
    
    if (companyToEdit?.logo) {
      setLogoPreview(companyToEdit.logo.path);
    }
  }, [companyToEdit]);

  // Add phone formatting function
  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const phoneNumber = value.replace(/[^\d]/g, '');
    if (phoneNumber.length < 4) return phoneNumber;
    if (phoneNumber.length < 7) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    }
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
  };

  // Add phone input handler
  const handlePhoneInput = (e, field) => {
    const formatted = formatPhoneNumber(e.target.value);
    setValue(field, formatted);
  };
  
  const schema = yup.object().shape({
    name: yup.string().required('Company Name is required'),
    alias: yup.string().required('Company Alias is required'),
    // companyId: yup.string().required('Company ID is required'),
    address1: yup.string().required('Street Address 1 is required'),
    
    city: yup.string().required('City/Town is required'),
    state: yup.string().required('State/Province is required'),
    zipcode: yup.string().required('Zip/Postal Code is required'),
    country_id: yup.string().required('country_id is required'),
    primary_number: yup.string()
      .required('Primary Phone is required')
      .matches(phoneRegex, 'Phone number must be in format (xxx) xxx-xxxx'),
    secondary_number: yup.string().nullable()
    .test('is-valid-phone', 'Phone number must be in format (xxx) xxx-xxxx', function (value) {
        if (!value) return true; // allow null or empty
        return phoneRegex.test(value);
      }),
    primary_email: yup.string()
      .required('Primary Email is required')
      .matches(emailRegex, 'Invalid email address'),
    secondary_email: yup.string().nullable()
    .test('is-valid-email', 'Invalid email address', function (value) {
        if (!value) return true; // allow null or empty
        return emailRegex.test(value);
      }),
    primary_contact_first_name: yup.string().required('Primary Contact First Name is required'),
    primary_contact_last_name: yup.string().required('Primary Contact Last Name is required'),
    //secondary_contact_first_name: yup.string(),
    // secondary_contact_last_name: yup.string(),
    // business_number: yup.string(),
    slogan: yup.string().required('Company Slogan is required')
  });
  const getDefaultValues = (companyToEdit = {}, user = {}) => ({
    name: companyToEdit?.name || '',
    alias: companyToEdit?.alias || '',
    companyId: companyToEdit?.id  || '',
    address1: companyToEdit?.address1 || '',
    address2: companyToEdit?.address2 || '',
    city: companyToEdit?.city || '',
    state: companyToEdit?.state || '',
    zipcode: companyToEdit?.zipcode || '',
    country_id: companyToEdit?.country_id || '',
    primary_number: companyToEdit?.primary_number || '',
    secondary_number: companyToEdit?.secondary_number || '',
    primary_email: companyToEdit?.primary_email || '',
    secondary_email: companyToEdit?.secondary_email || '',
    primary_contact_first_name: companyToEdit?.primary_contact_first_name || '',
    primary_contact_last_name:  companyToEdit?.primary_contact_last_name || '',
    secondary_contact_first_name: companyToEdit?.secondary_contact_first_name || '',
    secondary_contact_last_name: companyToEdit?.secondary_contact_last_name || '',
    business_number: companyToEdit?.business_number || '',
    slogan: companyToEdit?.slogan || '',
    logo: companyToEdit?.logo || '',
    
  })

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    reset
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getCompanyInfoById(id), user)
  });

  // Reset form when company data changes
  React.useEffect(() => {
    if (companyToEdit) {
      reset(companyToEdit);
      // Also set the logo preview
      if (companyToEdit.logo) {
        setLogoPreview(companyToEdit.logo.path);
      }
    }
  }, [companyToEdit, reset]);

  useEffect(() => {
    if(!id){
      reset(getDefaultValues({}, user));
      setLogoPreview('')
    }
  }, [id, reset, user]);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        Swal.fire({
          title: 'Error',
          text: 'File size should not exceed 5MB',
          icon: 'error'
        });
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
        
        setValue('logo', file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteLogo = () => {
    setLogoPreview('');
    setValue('remove_logo', true);
    setValue('logo', '');
  };
  setValue('companyId', companyToEdit?.id);
   
  const onSubmit = async (data) => {
   
    setIsLoading(true);
    showLoading();
    try {
      
      if (id) {
        
        // Update existing company
        const response = await updateCompanyInfo(id,data);
        
        if (response.status === 200) {
          toast.success('Company Information updated successfully!');
          reset();
          navigate(getRoutePath('/company-info'));
         
          // Swal.fire({
          //   title: 'Success',
          //   text: 'Company Information updated successfully!',
          //   icon: 'success'
          // }).then(() => {
          //   // Navigate after success
          //   navigate('/company-info');
          // });
          
        } else {
          
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
          
        }
        setIsLoading(false);
        hideLoading();
      } else {
        
        const response = await addCompanyInfo(data);
        if (response.status === 200) {
          toast.success('Company Information created successfully!');
          reset();
          navigate(getRoutePath('/company-info'));
         
          
          // Swal.fire({
          //   title: 'Success',
          //   text: 'Company Information created successfully!',
          //   icon: 'success'
          // }).then(() => {
          //   // Navigate after success
          //   navigate('/company-info');
          // });
         
        } else {
          
          if (response.errors) {
            Object.entries(response.errors).forEach(([field, message]) => {
              setError(field, {
                type: 'manual',
                message,
              });
            });
          } else if (response.error?.message) {
            setError('general', {
              type: 'manual',
              message: response.error.message,
            });
          }
          
        }
        setIsLoading(false);
        hideLoading();
        
      }
    } catch (error) {
      setIsLoading(false);
      hideLoading();
      console.error('Error saving company:', error);
      toast.error('Failed to save company information. Please try again.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
        
        <Breadcrumb />
        <div className="  mx-auto">
            <h1 className="text-2xl font-bold text-gray-900 mb-6">
            {id ? 'Edit Company Information' : 'Create New Company Information'}
            </h1>
        </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Company Information</h2>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" encType="multipart/form-data">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Company Details */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Name *</label>
                <input
                  type="text"
                  {...register('name')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.name ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Company Name"
                />
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company Alias *</label>
                <input
                  type="text"
                  {...register('alias')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.alias ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Company Alias"
                  readOnly={!!id}
                  disabled={!!id}
                />
                {errors.alias && (
                  <p className="mt-1 text-sm text-red-600">{errors.alias.message}</p>
                )}
              </div>
              {id && (       
              <div>
                <label className="block text-sm font-medium text-gray-700">Company ID *</label>
                <input
                  type="text"
                  {...register('companyId')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.companyId ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Company ID"
                  readOnly={!!id}
                  disabled={!!id}
                />
                {errors.companyId && (
                  <p className="mt-1 text-sm text-red-600">{errors.companyId.message}</p>
                )}
              </div>
              )}
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address 1 *</label>
                <textarea
                  {...register('address1')}
                  rows="2"
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.address1 ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Street Address 1"
                />
                {errors.address1 && (
                  <p className="mt-1 text-sm text-red-600">{errors.address1.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Street Address 2</label>
                <textarea
                  {...register('address2')}
                  rows="2"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter Street Address 2"
                />
                {errors.address2 && (
                  <p className="mt-1 text-sm text-red-600">{errors.address2.message}</p>
                )}          
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">City/Town *</label>
                  <input
                    type="text"
                    {...register('city')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.city ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter City/Town"
                  />
                  {errors.city && (
                    <p className="mt-1 text-sm text-red-600">{errors.city.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">State/Province *</label>
                  <input
                    type="text"
                    {...register('state')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.state ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter State/Province"
                  />
                  {errors.state && (
                    <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Zip/Postal Code *</label>
                  <input
                    type="text"
                    {...register('zipcode')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.zipcode ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Zip/Postal Code"
                  />
                  {errors.zipcode && (
                    <p className="mt-1 text-sm text-red-600">{errors.zipcode.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Country *</label>

                  <select
                    {...register('country_id')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.country_id ? 'border-red-500' : ''
                    }`}
                  >
                    <option value="">Select Country</option>
                    {countries.map((country) => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                     
                  </select>
                  {errors.country_id && (
                    <p className="mt-1 text-sm text-red-600">{errors.country_id.message}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Phone *</label>
                <input
                  type="tel"
                  {...register('primary_number')}
                  placeholder="(xxx) xxx-xxxx"
                  maxLength="14"
                  onChange={(e) => handlePhoneInput(e, 'primary_number')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.primary_number ? 'border-red-500' : ''
                  }`}
                />
                {errors.primary_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.primary_number.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Secondary Phone</label>
                <input
                  type="tel"
                  {...register('secondary_number')}
                  placeholder="(xxx) xxx-xxxx"
                  maxLength="14"
                  onChange={(e) => handlePhoneInput(e, 'secondary_number')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.secondary_number ? 'border-red-500' : ''
                  }`}
                />
                {errors.secondary_number && (
                  <p className="mt-1 text-sm text-red-600">{errors.secondary_number.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Primary Email *</label>
                <input
                  type="email"
                  {...register('primary_email')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.primary_email ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Primary Email"
                />
                {errors.primary_email && (
                  <p className="mt-1 text-sm text-red-600">{errors.primary_email.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Secondary Email</label>
                <input
                  type="email"
                  {...register('secondary_email')}
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.secondary_email ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Secondary Email"
                />
                {errors.secondary_email && (
                  <p className="mt-1 text-sm text-red-600">{errors.secondary_email.message}</p>
                )}
              </div>
            </div>

            {/* Contact Names */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Primary Contact First Name *</label>
                  <input
                    type="text"
                    {...register('primary_contact_first_name')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.primary_contact_first_name ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Primary Contact First Name"
                  />
                  {errors.primary_contact_first_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.primary_contact_first_name.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Primary Contact Last Name *</label>
                  <input
                    type="text"
                    {...register('primary_contact_last_name')}
                    className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                      errors.primary_contact_last_name ? 'border-red-500' : ''
                    }`}
                    placeholder="Enter Primary Contact Last Name"
                  />
                  {errors.primary_contact_last_name && (
                    <p className="mt-1 text-sm text-red-600">{errors.primary_contact_last_name.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Secondary Contact First Name</label>
                  <input
                    type="text"
                    {...register('secondary_contact_first_name')}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                    placeholder="Enter Secondary Contact First Name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Secondary Contact Last Name</label>
                  <input
                    type="text"
                    {...register('secondary_contact_last_name')}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                    placeholder="Enter Secondary Contact Last Name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">EIN/Business Number/License</label>
                <input
                  type="text"
                  {...register('business_number')}
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm"
                  placeholder="Enter EIN/Business Number/License"
                />
              </div>
            </div>

            {/* Company Logo and Slogan */}
            <div className="space-y-4">
                
              <div>
                <label className="block text-sm font-medium text-gray-700">Company Logo</label>
                <div className="mt-1 flex items-center space-x-4">
                    <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    {logoPreview && (
                        <div className="relative h-20">
                        <img
                            src={logoPreview}
                            alt="Company Logo Preview"
                            className="h-20 w-auto object-contain rounded"
                        />
                        <button
                            type="button"
                            onClick={handleDeleteLogo}
                            className="absolute top-0 right-0 -translate-y-2/2 translate-x-1/2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                        >
                            ×
                        </button>
                        </div>
                    )}
                </div>

                <p className="mt-1 text-sm text-gray-500">Max size: 500 × 300 px</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Company Slogan *</label>
                <textarea
                  {...register('slogan')}
                  rows="2"
                  className={`appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-[#009efb] focus:border-[#009efb] sm:text-sm ${
                    errors.slogan ? 'border-red-500' : ''
                  }`}
                  placeholder="Enter Company Slogan"
                />
                {errors.slogan && (
                  <p className="mt-1 text-sm text-red-600">{errors.slogan.message}</p>
                )}
              </div>
            </div>
          </div>
          {id ? 
            <>
            <div>
            <label className="block text-sm font-medium text-gray-700">Active</label>
            <input
              type="checkbox"
              {...register('active')}
               
              className="form-checkbox h-5 w-5 text-blue-600"
            />
            {errors.active && (
                  <p className="mt-1 text-sm text-red-600">{errors.active.message}</p>
                )}
            </div>
            </>
          : ' '}
          <div className="flex justify-end">
            <button
              type="submit"
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
             
              {isLoading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
               <>
                <SaveIcon className="w-4 h-4 mr-2" />
                {id ? 'Update' : 'Save'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyInfoForm; 